import { getAllExperts } from '@api/experts';
import { getAllLearningByFilterCriteria } from '@api/learningContent';
import { UserAction, useUserActionsHooks } from '@api/userActionsApiHandler';
import ActiveIcon from '@assets/images/icons/active-icon.svg';
import DoubleTick from '@assets/images/icons/double-tick.svg';
import Card from '@components/Atoms/Card';
import Img from '@components/Atoms/Img';
import ModalContent from '@components/ModalContent';
import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { ExpertsResultsResponse } from '@interfaces/index';
import { Box, CardContent, Grid, Card as MuiCard, Skeleton } from '@mui/material';
import { LearningArticleCard, LoadingBackdrop, SystemButton } from '@shared/molecules';
import { ActionCard, CreateAction, ExpertsCarousel } from '@shared/organisms';
import { CoreLayout } from '@shared/templates';
import { getHourBufferCategory } from '@utils/date.util';
import { castResponse } from 'custom.d';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { RootState } from 'store';
import styles from './index.module.scss';

export const PlanPage: React.FunctionComponent = () => {
  const [posts, setPosts] = useState<{ id: number; innerImageUrl: string; title: string }[]>([]);
  const [loadingPosts, setLoadingPosts] = useState<boolean>(true);
  const [errorPosts, setErrorPosts] = useState<boolean>(false);
  const { isMobile } = useWindowDimensions();
  const [actionModalOpen, setActionModalOpen] = useState<boolean>(false);
  const [showCompletedActions, setShowCompletedActions] = useState<boolean>(false);

  const [experts, setExperts] = useState<ExpertsResultsResponse>({ results: [], total: 0 });
  const [expertsLoading, setExpertsLoading] = useState(false);

  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig);

  const {
    data: userActionsResponse,
    isLoading: isLoadingActions,
    error: actionsError,
    refetch: refetchActions,
  } = useUserActionsHooks.useUserActions({
    completed: showCompletedActions,
  });

  const userActions = userActionsResponse?.success ? userActionsResponse?.responseObject || [] : [];

  useEffect(() => {
    getExperts();
    getWpPosts();
  }, []);

  const getExperts = async () => {
    try {
      setExpertsLoading(true);

      const options = { id: null, page: 0, perPage: 100 };

      const response = await getAllExperts(
        null,
        options?.id,
        options?.page,
        options?.perPage,
        null,
      );

      if (response?.success) {
        const data = castResponse<ExpertsResultsResponse>(response);
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const userTimeZoneTime = moment.tz(userTimezone).format('YYYY-MM-DD HH:mm:ss');

        const sortedExperts = (data?.results || [])
          .map((expert) => {
            const zoneName = expert?.expertDetail?.zoneName;
            const expertTimeZoneTime = moment.tz(zoneName).format('YYYY-MM-DD HH:mm:ss');

            const differenceInMinutes = moment(expertTimeZoneTime).diff(
              moment(userTimeZoneTime),
              'minutes',
            );

            return {
              ...expert,
              hourBufferCategory: getHourBufferCategory(differenceInMinutes),
            };
          })
          .sort((a, b) => {
            if (a.hourBufferCategory !== b.hourBufferCategory) {
              return a.hourBufferCategory - b.hourBufferCategory;
            }

            return b.orderId - a.orderId;
          });

        setExperts({ results: sortedExperts, total: data?.total });
      } else {
        setExperts({ results: [], total: 0 });
      }
    } catch {
      setExperts({ results: [], total: 0 });
    } finally {
      setExpertsLoading(false);
    }
  };

  const getWpPosts = () => {
    const getPostsAPI = async () => {
      try {
        const response = await getAllLearningByFilterCriteria({
          order: 'DESC',
          orderBy: 'contentCreatedAt',
          page: 1,
          perPage: 60,
          questionIds: [],
          tagIds: [],
        });

        const modifiedData =
          response?.data?.map((dt) => ({
            id: dt.id,
            innerImageUrl: dt.imageUrl,
            title: dt.title,
          })) || [];

        setPosts(modifiedData.slice(0, 4));
        setLoadingPosts(false);
      } catch (err) {
        setLoadingPosts(false);
        setErrorPosts(true);
      }
    };

    getPostsAPI();
  };

  return (
    <CoreLayout title="Action Plans" navigationType="default" fullWidthEnabled>
      {isLoadingActions ? (
        <div className="container">
          {/* <LoadingBackdrop text="You’re doing great — preparing your next small wins..." /> */}

          <div className={styles.loadingContainer}>
            <Skeleton variant="rounded" width="50%" height={50} />
            <Skeleton variant="rounded" width="100%" height="100vh" />
          </div>
        </div>
      ) : (
        <>
          <div className={`container ${styles.desktopContainer}`}>
            <div className={styles.topSectionTitleContainer}>
              <h3 className={styles.improveTitle}>Your Wellbeing Plan</h3>

              <div className={styles.filterPlansBtn}>
                <SystemButton
                  type="button"
                  className={styles.hsSecondaryBtn}
                  variant="outlinedPrimarySkyBlue"
                  size="small"
                  onClick={() => {
                    setShowCompletedActions((prevState) => !prevState);
                  }}
                >
                  {!showCompletedActions ? (
                    <>
                      <span>Completed Actions</span>

                      <Img
                        src={DoubleTick}
                        alt="HS Docubletick Icon"
                        className={styles.hsStartIcon}
                      />
                    </>
                  ) : (
                    <>
                      <span>Active Actions</span>

                      <Img src={ActiveIcon} alt="HS Start Icon" className={styles.hsStartIcon} />
                    </>
                  )}
                </SystemButton>

                <div className={styles.primaryFullWidthBtn}>
                  <SystemButton
                    type="button"
                    size="small"
                    className={`${styles.hsPrimaryBtn} ${styles.fullWidthMobile}`}
                    onClick={() => setActionModalOpen(true)}
                  >
                    Add new action
                  </SystemButton>
                </div>
              </div>
            </div>

            <div className={styles.focusCardContainer}>
              <Card className={styles.focusCard}>
                <div className={styles.focusCardHeader}>
                  <h5 className={styles.cardTitle}>Placeholder text goes here......</h5>
                </div>
                <div className={styles.actionSection}>
                  {isLoadingActions ? (
                    <>
                      {[0, 1, 2, 3, 4, 5].map((item, index) => (
                        <MuiCard key={index} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              mb={2}
                            >
                              <Box flex={1}>
                                <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                                <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="60%" height={20} />
                              </Box>
                              <Skeleton
                                variant="rectangular"
                                width={80}
                                height={32}
                                sx={{ borderRadius: 1, ml: 2, bgcolor: '#beaaf3' }}
                              />
                            </Box>

                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box display="flex" gap={1}>
                                <Skeleton
                                  variant="rectangular"
                                  width={60}
                                  height={24}
                                  sx={{ borderRadius: 1, bgcolor: '#04bc00' }}
                                />
                                <Skeleton
                                  variant="rectangular"
                                  width={80}
                                  height={24}
                                  sx={{ borderRadius: 1, bgcolor: '#04bc00' }}
                                />
                              </Box>
                              <Skeleton variant="text" width={80} height={16} />
                            </Box>
                          </CardContent>
                        </MuiCard>
                      ))}
                    </>
                  ) : (
                    <>
                      {userActions.length > 0 ? (
                        userActions.map((item: UserAction, index: number) => {
                          const dueDate =
                            item.dueDays >= 0
                              ? `${Math.abs(item.dueDays)} days to go`
                              : `${Math.abs(item.dueDays)} days overdue`;

                          const overdue = item.dueDays < 0;

                          return (
                            <ActionCard
                              key={index}
                              actionData={item}
                              className={`${styles.actionCard} ${styles.calmLilac}`}
                              title={item.title || ''}
                              description={item.description || ''}
                              dueDate={dueDate}
                              completed={item.completed !== null}
                              overdue={overdue}
                              theme="#beaaf3"
                              actionButtonName={item.actionButtonTitle}
                              actionImage={item.actionImage}
                              handleRefetch={refetchActions}
                            />
                          );
                        })
                      ) : (
                        <h6 className={styles.noActionsText}>
                          {showCompletedActions
                            ? "You haven't completed any actions yet. Take a small step towards your actions today."
                            : 'No active actions'}
                        </h6>
                      )}
                    </>
                  )}
                </div>

                <div className={styles.blogPostSection}>
                  <h5>Recommended for you</h5>
                  <Grid container spacing={4} className={styles.learningItemsContainer}>
                    {posts &&
                      posts.length > 0 &&
                      posts.map((item, index: number) => (
                        <Grid item xs={12} md={3} key={index}>
                          <LearningArticleCard
                            data={item}
                            loading={loadingPosts}
                            sectionType={item.id === -999 ? 'allLearnings' : 'posts'}
                            navigateTo="oldPosts"
                          />
                        </Grid>
                      ))}
                  </Grid>

                  {posts?.length === 0 && !loadingPosts && (
                    <p className={styles.noPostsText}>No posts</p>
                  )}
                </div>

                {companyConfigData.data && companyConfigData.data.expertsEnabled && (
                  <div className={styles.expertSection}>
                    <h5>Book a session with an expert</h5>
                    <ExpertsCarousel experts={experts} loading={expertsLoading} />

                    {experts?.total === 0 && !expertsLoading && (
                      <p className={styles.noPostsText}>No experts</p>
                    )}
                  </div>
                )}
              </Card>
            </div>
          </div>

          <div className={styles.floatingActionButton} onClick={() => setActionModalOpen(true)}>
            <span>+</span>
          </div>
        </>
      )}

      <Modal
        isOpen={actionModalOpen}
        onRequestClose={() => setActionModalOpen(false)}
        contentLabel="Improve Action Modal"
        ariaHideApp={false}
        style={{
          content: {
            width: isMobile ? '90%' : undefined,
          },
        }}
      >
        <ModalContent
          onClose={() => setActionModalOpen(false)}
          hasBackground
          title="Create your action"
        >
          <CreateAction
            closeModal={() => setActionModalOpen(false)}
            improvePlan="Action name"
            id={0}
            handleRefetch={refetchActions}
          />
        </ModalContent>
      </Modal>
    </CoreLayout>
  );
};
